import { IconChevronLeft, IconChevronRight } from '@aws-amplify/ui-react/internal'
import { DoneOutline } from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import ButtonBar from '@/components/inputs/ButtonBar'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import BidOverviewButton from '@/features/bidding/components/BidOverviewButton'
import BidOfferTabs from '@/features/bidding/components/capacity/offer/BidOfferTabs'
import CountrySelector from '@/features/bidding/components/CountrySelector'
import CreateBidButton from '@/features/bidding/components/CreateBidButton'
import ExternalReferenceButtons from '@/features/bidding/components/ExternalReferenceButtons'
import SubmitButton from '@/features/bidding/components/SubmitButton'
import { BidOfferDataProvider, useBidOfferDataContext } from '@/features/bidding/contexts/BidOfferDataContext'
import { BiddingFlow } from '@/features/bidding/utils/getBiddingFlow'
import { anyBidOutOfBiddableVolumeRange } from '@/features/bidding/utils/validation/anyBidOutOfBiddableVolumeRange'

function BidOfferCapacity() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const {
    eligibleActivationGroups,
    latestDraftOrOfferedBids,
    biddingFlow,
    isCreateBidButtonDisabled,
    isValidating,
    handleCapacityFlowSubmit,
    handleCapacityAndPriceFlowSubmit,
  } = useBidOfferDataContext()

  const shouldShowConfirmation = anyBidOutOfBiddableVolumeRange(latestDraftOrOfferedBids, eligibleActivationGroups)

  return (
    <>
      <BidOfferHeader />
      <BidOfferTabs />

      <ButtonBar>
        <CustomButton
          key="backButton"
          startIcon={<IconChevronLeft />}
          variant={'outlined'}
          onClick={() => navigate(-1)}
        >
          {t('common.navigation.back')}
        </CustomButton>

        {canMutateBidding && biddingFlow === BiddingFlow.CAPACITY_FLOW && (
          <CreateBidButton
            dialogProps={{
              description: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.description'),
              title: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.title'),
            }}
            disabled={isCreateBidButtonDisabled}
            isValidatingRegulationObject={isValidating}
            shouldShowConfirmation={shouldShowConfirmation}
            onSubmit={handleCapacityFlowSubmit}
          />
        )}

        {canMutateBidding && biddingFlow === BiddingFlow.CAPACITY_AND_PRICE_FLOW && (
          <SubmitButton
            buttonProps={{
              startIcon: <IconChevronRight />,
              disabled: isCreateBidButtonDisabled,
              'data-testid': 'bid-prices-button',
            }}
            dialogProps={{
              description: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.description'),
              title: t('bidding.dialog.offered_volume_out_of_biddable_volume_range.title'),
            }}
            isLoading={isValidating}
            shouldShowConfirmation={shouldShowConfirmation}
            onSubmit={handleCapacityAndPriceFlowSubmit}
          >
            {t('bidding.create_bid.bid_prices')}
          </SubmitButton>
        )}
      </ButtonBar>
    </>
  )
}

function BidOfferHeader() {
  const { t } = useTranslation()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const { groupParams, portfolio, eligibleActivationGroups, isSavingAllDrafts, handleSaveAllClick } =
    useBidOfferDataContext()

  return (
    <PageHeader
      appBarContent={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Box display="flex" flexDirection="row" sx={{ flex: 1, justifyContent: 'left' }}>
            <CountrySelector disabled />
          </Box>

          <Stack flexDirection="row" gap={1}>
            <ExternalReferenceButtons marketProgram={groupParams.marketProgram} portfolio={portfolio} />

            {canMutateBidding && eligibleActivationGroups.length > 1 && (
              <CustomButton
                color="inheritWhite"
                disabled={isSavingAllDrafts}
                startIcon={<DoneOutline />}
                variant="outlined"
                onClick={handleSaveAllClick}
              >
                {t('bidding.create_bid.save_all_drafts')}
              </CustomButton>
            )}

            <BidOverviewButton />
          </Stack>
        </Box>
      }
      breadcrumbItems={[
        { text: t('component.page_header.bidding'), to: '/bidding' },
        { text: t('component.page_header.bid.todo_list'), to: '/bidding/todo-list' },
      ]}
      pageTitle={t('component.page_header.bid.create.label', {
        portfolio: groupParams.portfolioCode,
        marketProgram: t(`bidding.market_program.${groupParams.marketProgram}`),
        bidType: groupParams.bidType.toLowerCase(),
      })}
    />
  )
}

function BidOfferCapacityWithProvider() {
  return (
    <BidOfferDataProvider>
      <BidOfferCapacity />
    </BidOfferDataProvider>
  )
}

export default BidOfferCapacityWithProvider
